import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import {
  FRESH_DESK_CONFIG,
  FreshdeskConfig,
  Messaging,
} from './freshdesk.model';
import { BehaviorSubject } from 'rxjs';

export interface ICustomWindow extends Window {
  FreshworksWidget: any;
}

export interface FreshworksIdentity {
  name: string;
  email: string;
}

function getWindow(): any {
  return window;
}

const DEFAULT_MESSAGING = {
  banner: 'Mediamaker Support',
  launcher: 'Help',
  contact_form: {
    title: 'Contact Us',
    submit: 'Send',
    confirmation: 'Thank you for your feedback!',
  },
};

@Injectable()
export class WindowRefService {
  get nativeWindow(): ICustomWindow {
    return getWindow();
  }
}

@Injectable({
  providedIn: 'root',
})
export class FreshdeskService {
  private loaded$ = new BehaviorSubject<boolean>(false);
  private locale!: string;

  constructor(
    @Inject(FRESH_DESK_CONFIG) private config: FreshdeskConfig,
    @Inject(DOCUMENT) private document: Document,
    private windowRef: WindowRefService
  ) {}

  init(locale: string = 'en') {
    if (!this.config) {
      throw new Error('No Freshdesk config provided');
    }

    if (this.config.disabled) return;
    this.locale = locale;

    const windowScriptInnerHtml = this.createWindowScript(this.config.widgetId);
    const loaderScriptSrc = this.loaderScriptSrc(this.config.widgetId);

    const windowScript = this.document.createElement('script');
    const loaderScript = this.document.createElement('script');
    loaderScript.setAttribute('async', '');
    loaderScript.setAttribute('defer', '');

    windowScript.innerHTML = windowScriptInnerHtml;
    loaderScript.setAttribute('src', loaderScriptSrc);

    loaderScript.onload = () => {
      this.loaded$.next(true);
      this.hide();
    };

    this.document.body.appendChild(windowScript);
    this.document.body.appendChild(loaderScript);
  }

  boot() {
    this.windowRef.nativeWindow.FreshworksWidget('boot');
  }

  show(identity?: FreshworksIdentity) {
    if (this.config.disabled) return;

    this.setMessaging(this.config.messaging);

    this.windowRef.nativeWindow.FreshworksWidget('show', 'launcher');
    identity &&
      this.windowRef.nativeWindow.FreshworksWidget('identify', 'ticketForm', {
        ...identity,
        custom_fields: { cf_environment: this.config.environment },
      });
  }

  hide() {
    if (this.config.disabled) return;

    this.windowRef.nativeWindow.FreshworksWidget('identify', 'ticketForm', {
      name: '',
      email: '',
    });
    this.windowRef.nativeWindow.FreshworksWidget('hide', 'launcher');
  }

  close() {
    if (this.config.disabled) return;

    this.windowRef.nativeWindow.FreshworksWidget('close');
  }

  destroy() {
    if (this.config.disabled) return;

    this.windowRef.nativeWindow.FreshworksWidget('destroy');
  }

  private createWindowScript(widgetId: string) {
    return `
    window.fwSettings = {
      widget_id: ${widgetId},
      locale: '${this.locale}',
    };
      !(function () {
        if ('function' != typeof window.FreshworksWidget) {
          var n = function () {
            n.q.push(arguments);
          };
          (n.q = []), (window.FreshworksWidget = n);
        }
      })();
    `;
  }

  private loaderScriptSrc(widgetId: string) {
    return `https://euc-widget.freshworks.com/widgets/${widgetId}.js`;
  }

  private setMessaging(messaging?: Messaging) {
    this.windowRef.nativeWindow.FreshworksWidget('setLabels', {
      [this.locale]: messaging || DEFAULT_MESSAGING,
    });
  }
}
